import { ChevronDown, ChevronRight, Clock, FileText, User } from 'lucide-react';
import { useState } from 'react';

import { usePlans } from '../hooks/use-plans';
import { formatRelativeTime } from '../lib/format-relative-time';
import { Badge } from './Badge';
import { Button } from './Button';

import type { PlanResponse } from '../lib/api-client';

type PlanStatus = PlanResponse['status'];

const STATUS_BADGE: Record<PlanStatus, { variant: 'success' | 'info' | 'warning' | 'error' | 'not-started'; label: string }> = {
  draft: { variant: 'not-started', label: 'Draft' },
  pending_approval: { variant: 'warning', label: 'Pending' },
  approved: { variant: 'success', label: 'Approved' },
  executing: { variant: 'info', label: 'Executing' },
  completed: { variant: 'success', label: 'Completed' },
  revised: { variant: 'warning', label: 'Revised' },
  denied: { variant: 'not-started', label: 'Cancelled' },
};

function PlansViewSkeleton() {
  return (
    <div data-testid="plans-skeleton" className="flex flex-col gap-sd-1 p-sd-3 animate-pulse">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex items-center gap-sd-3 px-sd-3 py-sd-2">
          <div className="w-16 h-5 bg-sd-border/30 rounded-sd-sm" />
          <div className="flex-1 h-4 bg-sd-border/20 rounded" />
          <div className="w-12 h-4 bg-sd-border/20 rounded" />
          <div className="w-20 h-4 bg-sd-border/20 rounded" />
        </div>
      ))}
    </div>
  );
}

function PlanRow({ plan }: { plan: PlanResponse }) {
  const [expanded, setExpanded] = useState(false);
  const badge = STATUS_BADGE[plan.status] ?? STATUS_BADGE.draft;

  const steps = Array.isArray(plan.steps)
    ? (plan.steps as Array<string | Record<string, string>>).map((s) =>
        typeof s === 'string' ? s : (s.description ?? s.action ?? JSON.stringify(s))
      )
    : [];

  const dataAccess = Array.isArray(plan.data_access)
    ? (plan.data_access as Array<Record<string, string>>).map((item) => ({
        system: item.service_name ?? item.system ?? '',
        data: item.reason ?? item.data ?? '',
        accessLevel: (item.access_level ?? 'read') as string,
      }))
    : [];

  // Derive a summary from first step or plan metadata
  const summary = steps[0]?.trim() ?? `Plan ${plan.id}`;

  return (
    <>
      <div
        role="row"
        className="flex items-center gap-sd-3 px-sd-3 py-sd-2 border-b border-sd-border/20 text-sd-sm hover:bg-sd-surface-elevated/40 transition-colors duration-100 cursor-pointer"
        onClick={() => setExpanded((p) => !p)}
      >
        <div className="shrink-0 w-20">
          <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
        </div>
        <span className="min-w-0 flex-1 truncate text-sd-text">
          {summary}
        </span>
        <span className="w-12 text-sd-xs text-sd-text-secondary text-center hidden @md:inline">
          {steps.length} {steps.length === 1 ? 'step' : 'steps'}
        </span>
        <span className="w-20 text-sd-xs text-sd-text-secondary shrink-0 text-right">
          {formatRelativeTime(plan.created_at)}
        </span>
        <span className="shrink-0 w-4">
          {expanded
            ? <ChevronDown size={14} className="text-sd-text-muted" aria-hidden="true" />
            : <ChevronRight size={14} className="text-sd-text-muted" aria-hidden="true" />
          }
        </span>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-sd-4 py-sd-3 bg-sd-surface-elevated/20 border-b border-sd-border/20">
          {/* Approval metadata */}
          {(plan.approved_by || plan.approved_at) && (
            <div className="flex flex-wrap items-center gap-sd-3 text-sd-xs text-sd-text-secondary mb-sd-3">
              {plan.approved_by && (
                <span className="flex items-center gap-sd-1">
                  <User size={12} aria-hidden="true" />
                  <span>Approved by {plan.approved_by}</span>
                </span>
              )}
              {plan.approved_at && (
                <span className="flex items-center gap-sd-1">
                  <Clock size={12} aria-hidden="true" />
                  <span>{formatRelativeTime(plan.approved_at)}</span>
                </span>
              )}
            </div>
          )}

          {plan.denial_reason && (
            <div className="text-sd-xs text-sd-text-secondary mb-sd-3">
              Reason: {plan.denial_reason}
            </div>
          )}

          {/* Steps */}
          {steps.length > 0 && (
            <div className="mb-sd-3">
              <h4 className="text-sd-xs font-semibold uppercase tracking-wide text-sd-text-secondary mb-sd-2">Plan Steps</h4>
              <div className="flex flex-col gap-sd-1">
                {steps.map((step, i) => {
                  const trimmed = typeof step === 'string' ? step.trim() : String(step);
                  if (!trimmed) return null;
                  return (
                    <div key={i} className="flex gap-sd-2 text-sd-xs text-sd-text leading-relaxed">
                      <span className="flex items-center justify-center w-[18px] h-[18px] rounded-sd-full bg-sd-surface-elevated text-[10px] font-medium text-sd-text-secondary shrink-0 mt-[1px]">{i + 1}</span>
                      <span>{trimmed}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Data access */}
          {dataAccess.length > 0 && (
            <div>
              <h4 className="text-sd-xs font-semibold uppercase tracking-wide text-sd-text-secondary mb-sd-2">Data Access</h4>
              <div className="flex flex-col gap-sd-1">
                {dataAccess.map((item, i) => (
                  <div key={i} className="flex items-center gap-sd-2 text-sd-xs text-sd-text">
                    <span className="font-medium">{item.system}</span>
                    <span className="text-sd-text-secondary">{item.data}</span>
                    <span className={`inline-flex items-center px-sd-1 py-0.5 rounded-sd-full text-[10px] font-medium ${item.accessLevel === 'read' ? 'bg-sd-info/10 text-sd-info' : 'bg-sd-warning/10 text-sd-warning'}`}>
                      {item.accessLevel === 'read' ? 'Read' : 'R/W'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export interface PlansViewProps {
  enabled?: boolean;
}

export function PlansView({ enabled = true }: PlansViewProps) {
  const { plans, status, error, hasMore, loadMore, reload } = usePlans({ enabled });

  if (status === 'loading') {
    return <PlansViewSkeleton />;
  }

  if (status === 'error') {
    return (
      <div className="flex flex-col items-center justify-center h-full p-sd-4 text-center">
        <p className="text-sd-error mb-sd-3">{error ?? 'Failed to load plans'}</p>
        <Button variant="primary" onClick={reload}>Retry</Button>
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div data-testid="plans-empty" className="flex flex-col items-center justify-center flex-1 p-sd-4 text-center">
        <div className="w-12 h-12 rounded-sd-lg bg-sd-info/10 flex items-center justify-center mb-sd-3 sd-reveal-1">
          <FileText size={24} className="text-sd-info/60" />
        </div>
        <p className="text-sd-lg font-semibold text-sd-text mb-sd-1 sd-reveal-2">No plans yet</p>
        <p className="text-sd-sm text-sd-text-muted max-w-xs sd-reveal-3">
          Plans will appear here when agents request credential access for tasks.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header row */}
      <div role="row" className="flex items-center gap-sd-3 px-sd-3 py-sd-1 border-b border-sd-border/30 text-sd-xs font-medium text-sd-text-secondary uppercase tracking-wider bg-sd-surface-elevated/40">
        <span className="w-20">Status</span>
        <span className="flex-1">Summary</span>
        <span className="w-12 text-center hidden @md:inline">Steps</span>
        <span className="w-20 text-right">Created</span>
        <span className="w-4" />
      </div>

      {/* Rows */}
      <div role="table" aria-label="Plans" className="flex-1 overflow-y-auto">
        {plans.map((plan) => (
          <PlanRow key={plan.id} plan={plan} />
        ))}
      </div>

      {/* Load more */}
      {hasMore && (
        <div className="flex justify-center py-sd-2 border-t border-sd-border/20">
          <Button variant="ghost" size="sm" onClick={loadMore}>
            <FileText size={14} className="mr-sd-1" /> Load more
          </Button>
        </div>
      )}
    </div>
  );
}
